import React, { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Autocomplete, TextField } from '@mui/material'
import { useGetMaterialTypesQuery, useGetMaterialTypeQuery, useGetMaterialTypeLazyQuery } from 'services/materialTypes'
import { useSelector } from 'react-redux'
import { formatMaterialName } from 'utils/data-manipulation/valueFormatter'

const MaterialAutocomplete = ({
  defaultValue,
  onChange,
  variant,
  size,
  placeholder,
  label,
  InputProps,
  onUpdateMaterial,
  preventDuplicateMaterials,
  apiRef,
  canAutoSelect,
  useSupplementalPriceSheetIds,
  canOverridePrices,
  TextFieldProps,
  orderData,
  isFinishedGoodOrPackingList,
  ...restProps
}) => {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(null)
  const [searchValue, setSearchValue] = useState('')
  const { supplementalPriceSheetIds } = useSelector(state => state.materials)
  const hasEditedField = useRef(false)

  const { data: materialTypesData, isFetching: isFetchingMaterialTypes } = useGetMaterialTypesQuery(
    {
      filter: JSON.stringify({
        search: searchValue,
        priceSheetId: orderData?.priceSheet?.id || '',
        ...(supplementalPriceSheetIds?.length > 0 && { supplementalPriceSheetIds }),
      }),
      pageSize: 50,
    },
    { skip: !open },
  )

  const materialDropdownOptions = useMemo(() => {
    if (!materialTypesData?.Items) return []
    if (!orderData) return materialTypesData?.Items
    return orderData?.priceSheet?.entriesObj?.map(entry => {
      const material = materialTypesData.Items.find(material => material.id === entry.materialTypeId)
      return {
        ...material,
        orderEntryId: entry.id,
        overrideName: entry.overrideName,
        supplementalPriceEntry: entry,
      }
    })
  }, [orderData, materialTypesData])

  const getMaterialTypeByIdPayload = useMemo(() => {
    const payload = { materialTypeId: defaultValue?.materialTypeId }
    if (supplementalPriceSheetIds?.length > 0) {
      payload.filter = JSON.stringify({ supplementalPriceSheetIds })
    }
    return payload
  }, [defaultValue?.materialTypeId, supplementalPriceSheetIds])

  const { data: materialTypeData, isLoading: isLoadingMaterialTypeData } = useGetMaterialTypeQuery(
    getMaterialTypeByIdPayload,
    { skip: !value || !!value?.id || !defaultValue?.materialTypeId, refetchOnMountOrArgChange: true },
  )

  const [getMaterialType, { data: materialTypeLazyData, isLoading: isLoadingMaterialTypeLazyData }] =
    useGetMaterialTypeLazyQuery()

  useEffect(() => {
    if (!value?.id) return
    if (!useSupplementalPriceSheetIds) return
    getMaterialType(getMaterialTypeByIdPayload)
  }, [supplementalPriceSheetIds])

  const orderEntryData = useMemo(() => {
    if (!defaultValue?.orderEntryId) return null
    return orderData?.priceSheet?.entriesObj?.find(entry => entry.id === defaultValue.orderEntryId)
  }, [orderData, defaultValue?.orderEntryId])

  useEffect(() => {
    // only run this useEffect if the user has NOT changed the material
    if (hasEditedField.current) return
    if (defaultValue.orderEntryId && !orderEntryData) return
    if (materialTypeLazyData) {
      const newValue = {
        ...materialTypeLazyData,
        ...(orderEntryData && {
          orderEntryId: orderEntryData?.id,
          overrideName: orderEntryData?.overrideName,
          supplementalPriceEntry: orderEntryData,
        }),
      }
      setValue(newValue)
      onChange(newValue, canOverridePrices ? 'price override' : 'load price entry')
    }
  }, [materialTypeLazyData, orderEntryData])

  useEffect(() => {
    if (open) return
    setSearchValue('')
  }, [open, value])

  useEffect(() => {
    // only run this useEffect if the user has NOT changed the material
    if (hasEditedField.current) return
    if (defaultValue.orderEntryId && !orderEntryData) return
    if (materialTypeData) {
      const newValue = {
        ...materialTypeData,
        ...(orderEntryData && {
          orderEntryId: orderEntryData?.id,
          overrideName: orderEntryData?.overrideName,
          supplementalPriceEntry: orderEntryData,
        }),
      }
      setValue(newValue)
      onChange(newValue)
    }
  }, [materialTypeData, orderEntryData])

  useEffect(() => {
    // only run this useEffect if the user HAS changed the material
    if (!hasEditedField.current) return
    if (defaultValue.orderEntryId && !orderEntryData) return
    setValue(previousValue => {
      const newValue = {
        ...previousValue,
        orderEntryId: defaultValue.orderEntryId ? orderEntryData?.id : null,
        overrideName: defaultValue.orderEntryId ? orderEntryData?.overrideName : '',
        ...(defaultValue.orderEntryId &&
          orderEntryData && {
            supplementalPriceEntry: orderEntryData,
          }),
      }
      onChange(newValue, canOverridePrices ? 'price override' : 'load price entry')
      return newValue
    })
  }, [orderEntryData])

  useEffect(() => {
    if (defaultValue?.materialTypeId !== value?.id && defaultValue?.materialTypeId !== value?.materialTypeId) {
      setValue(defaultValue?.materialTypeId ? defaultValue : null)
    }
  }, [defaultValue?.materialTypeId])

  const isLoading = isFetchingMaterialTypes || isLoadingMaterialTypeData || isLoadingMaterialTypeLazyData

  const filterOptions = fullOptions => {
    if (isFinishedGoodOrPackingList) {
      return fullOptions.filter(option => option.id === defaultValue.materialTypeId)
    }
    const filteredOptions = fullOptions.filter(option => {
      const optionInOrderData = orderData
        ? orderData?.priceSheet?.entriesObj?.find(entry => entry.materialTypeId === option.id)
        : true
      const optionIsNotDuplicate = preventDuplicateMaterials
        ? !Array.from(apiRef?.current?.getRowModels()?.values() || {})?.some(
            material => material.materialTypeId === option.id,
          )
        : true
      return option?.id === value?.id || (!!option.defaultPriceEntry && optionInOrderData && optionIsNotDuplicate)
    })
    return filteredOptions
  }

  return (
    <Autocomplete
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      fullWidth
      value={value}
      options={isFetchingMaterialTypes ? [] : materialDropdownOptions}
      filterOptions={filterOptions}
      renderOption={(props, option) => (
        <li {...props} key={`${option.id}-${option.orderEntryId}`}>
          {formatMaterialName(option)}
        </li>
      )}
      loading={isLoading}
      getOptionLabel={formatMaterialName}
      isOptionEqualToValue={(option, selectedValue) => {
        if (selectedValue.orderEntryId) {
          return option.orderEntryId === selectedValue.orderEntryId
        }
        return option.id === selectedValue.id
      }}
      onChange={(_, newValue, reason) => {
        hasEditedField.current = true
        onUpdateMaterial()
        setValue(newValue)
        onChange(newValue, reason)
      }}
      onInputChange={(_, newSearchValue) => {
        setSearchValue(newSearchValue)
      }}
      renderInput={params => (
        <TextField
          {...params}
          variant={variant}
          label={label}
          size={size}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            ...InputProps,
          }}
          {...TextFieldProps}
        />
      )}
      selectOnFocus={canAutoSelect}
      autoHighlight={canAutoSelect}
      {...restProps}
    />
  )
}

MaterialAutocomplete.propTypes = {
  defaultValue: PropTypes.object,
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
  size: PropTypes.oneOf(['medium', 'small']),
  control: PropTypes.instanceOf(Object),
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  InputProps: PropTypes.object,
  onUpdateMaterial: PropTypes.func,
  preventDuplicateMaterials: PropTypes.bool,
  apiRef: PropTypes.object,
  canAutoSelect: PropTypes.bool,
  useSupplementalPriceSheetIds: PropTypes.bool,
  canOverridePrices: PropTypes.bool,
  TextFieldProps: PropTypes.object,
  orderData: PropTypes.object,
  isFinishedGoodOrPackingList: PropTypes.bool,
}

MaterialAutocomplete.defaultProps = {
  defaultValue: {},
  onChange: () => {},
  variant: 'standard',
  size: 'medium',
  control: null,
  name: null,
  label: null,
  placeholder: null,
  InputProps: null,
  TextFieldProps: null,
  onUpdateMaterial: () => {},
  preventDuplicateMaterials: false,
  apiRef: {},
  canAutoSelect: false,
  useSupplementalPriceSheetIds: false,
  canOverridePrices: false,
  orderData: null,
  isFinishedGoodOrPackingList: false,
}

export default MaterialAutocomplete
